import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import { ResponsiveActionButton } from 'components/Buttons'
import PMBBadge from 'components/PMBBadge'
import { TableComponent as DataTable } from 'components/TableComponent'
import { capitalizeEveryWord } from 'helpers'
import { useEffect } from 'react'
import { ChevronDown, ChevronLeft } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_EMPLOYEE_PAYMENTS_AUTO_CALCULATION, URL_WAGE_SALARY } from 'utils/pathUrl'
import DisbursementModal from '../employee-payments/DisbursementModal'
import { useFetchTeamListQuery } from '../employee-payments/employeePaymentsListApiSlice'
import {
  resetDisbursementByEmployeeId,
  setDisbursementDataDisburses,
  setEmployeeEdit,
  setIsOpenModalManual,
} from '../employee-payments/employeePaymentsListSlice'
import FooterConfirmation from '../employee-payments/FooterConfirmation'

const transformTeamToDisburses = (teamData) => {
  if (!teamData) return null

  return {
    batchName: '',
    disburses: teamData?.map((item) => ({
      unit: 0,
      employeeUserId: item.userId,
      employeeName: item.name,
      nameItem: '',
      prIds: '',
      itemId: null,
      amount: 0,
      disburseMethod: '',
      notes: '',
      proofImgs: '',
      processreportIds: [],
      rolesName: item.rolesName,
    })),
  }
}

const WageSalaryCreate = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { tag, disbursementData, isOpenModalManual, employeeEdit } = useSelector(
    (state) => state.employeePaymentsList
  )

  const { data: teamData, isLoading, isFetching } = useFetchTeamListQuery(
    {
      page: tag?.page,
      size: tag?.size,
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
      s: tag?.search,
    },
    { refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    if (teamData?.items) {
      const formattedData = transformTeamToDisburses(teamData?.items)

      if (!disbursementData?.disburses?.length) {
        dispatch(setDisbursementDataDisburses(formattedData))
      }
    }
  }, [teamData?.items, dispatch])

  const disburses = disbursementData?.disburses || []

  const columns = [
    {
      name: <span>Name</span>,
      wrap: true,
      grow: 2,
      cell: (row) => row?.employeeName || row?.name || '',
    },
    {
      name: <span>Role</span>,
      wrap: true,
      grow: 2,
      cell: (row) => row?.rolesName || row?.role || '',
    },
    {
      name: <span>Amount</span>,
      wrap: true,
      grow: 2,
      right: true,
      cell: (row) => <div>Rp{newFormatThousandSeparator(row?.amount || 0, 0)}</div>,
    },
    {
      name: <span>Payment Method</span>,
      wrap: true,
      grow: 2,
      cell: (row) => {
        if (!row?.disburseMethod) return '-'
        return (
          <>
            {row?.disburseMethod === 'CASH' && (
              <PMBBadge
                color="light"
                textColor="#3A3A3C"
                title="Tunai"
                className="px-2 w-auto"
                size="small"
              />
            )}
            {row?.disburseMethod !== 'CASH' && (
              <PMBBadge
                color="light"
                textColor="#3A3A3C"
                title={capitalizeEveryWord(row?.disburseMethod)}
                className="px-2 w-auto"
                size="small"
              />
            )}
          </>
        )
      },
    },
    {
      name: <span>Note</span>,
      wrap: true,
      grow: 2,
      cell: (row) => row?.notes || '',
    },
    {
      name: '',
      wrap: true,
      width: '100px',
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center justify-content-end">
          {row?.amount > 0 ? (
            <div className="d-flex align-items-center gap-2">
              <ResponsiveActionButton
                iconName="pencil"
                label="Edit"
                onClick={() => {
                  dispatch(setEmployeeEdit(row))
                  dispatch(setIsOpenModalManual(true))
                }}
              />
              <ResponsiveActionButton
                key="delete"
                iconName="trash"
                label="Clear"
                onClick={() => {
                  dispatch(resetDisbursementByEmployeeId(row?.employeeUserId))
                }}
              />
            </div>
          ) : (
            <ResponsiveActionButton
              iconName="plus-circle-outline"
              label="Add Manual"
              onClick={() => {
                dispatch(setEmployeeEdit(row))
                dispatch(setIsOpenModalManual(true))
              }}
            />
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="w-100 px-3 pb-5 mb-5">
      <div className="mt-3">
        <BreadCrumbs
          breadCrumbActive="Available Employees"
          breadCrumbParent="Wage & Salary"
          breadCrumbParentLink={URL_WAGE_SALARY}
        />
      </div>

      {/* HEADER SECTION */}
      <Row className="mb-4 align-items-center g-3">
        <Col xs="12" md="7" lg="8">
          <div className="d-flex align-items-center gap-2">
            <Button
              color="link"
              className="p-0 text-dark"
              onClick={() => navigate(URL_WAGE_SALARY)}
            >
              <ChevronLeft size={24} />
            </Button>
            <h5 className="mb-0 fw-bold" style={{ fontSize: '24px' }}>
              Available Employees
            </h5>
          </div>
        </Col>
        <Col xs="12" md="5" lg="4" className="d-flex justify-content-md-end">
          <Button
            color="primary"
            className="px-4 py-2 fw-semibold shadow-sm"
            style={{
              borderRadius: '8px',
              backgroundColor: '#02678D',
              borderColor: '#02678D',
            }}
            onClick={() => navigate(URL_EMPLOYEE_PAYMENTS_AUTO_CALCULATION)}
          >
            Auto Calculate
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-3">
        <CardBody className="p-4">
          <P className="mb-3 fw-semibold text-dark" size="medium">
            Available Employees
          </P>
          <div>
            <DataTable
              columns={columns}
              data={disburses}
              pagination
              paginationServer
              sortServer
              responsive
              persistTableHead
              progressPending={isLoading}
              progressComponent={
                <div style={{ margin: '100px 0px' }}>
                  <Spinner color="primary" />
                </div>
              }
              className="border p-0 border-1 rounded-top"
              noDataComponent={
                <p className="my-3 fw-semibold">
                  {!isLoading && !isFetching
                    ? 'Belum ada data yang didaftarkan'
                    : 'Loading karyawan...'}
                </p>
              }
              sortIcon={<ChevronDown />}
              paginationComponent={() => <></>}
            />
          </div>
        </CardBody>
      </Card>

      <DisbursementModal
        isOpen={isOpenModalManual}
        employee={employeeEdit}
        showDateInput={true}
      />
      <FooterConfirmation variant="wage-salary" />
    </div>
  )
}

export default WageSalaryCreate
